/**
 * Icon.tsx — egne SVG-ikon-komponenter
 *
 * Ren React SVG — ingen avhengighet av @carbon/web-components eller @carbon/icons.
 * SVG-pathene er kopiert fra @carbon/icons v11.84.0 (kilde, ikke runtime-avhengighet).
 * Alle ikoner bruker fill="currentColor" og arver farge fra forelderen.
 *
 * Ikonkatalog:
 * 1. SearchIcon — forstørrelsesglass (søk)
 * 2. NotificationIcon — bjelle (varsler)
 * 3. SwitcherIcon — 3x3 rutenett (app switcher)
 * 4. MenuIcon — hamburgerlinjer (meny)
 */

import { SVGProps } from 'react';

/* =========================================================================
 * 1. SearchIcon — viewBox 0 0 32 32
 * ========================================================================= */
export function SearchIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M29,27.5859l-7.5521-7.5521a11.0177,11.0177,0,1,0-1.4141,1.4141L27.5859,29ZM4,13a9,9,0,1,1,9,9A9.01,9.01,0,0,1,4,13Z" />
    </svg>
  );
}

/* =========================================================================
 * 2. NotificationIcon — viewBox 0 0 32 32
 * ========================================================================= */
export function NotificationIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M28.7071,19.293,26,16.5859V13a10.0136,10.0136,0,0,0-9-9.9492V1H15V3.0508A10.0136,10.0136,0,0,0,6,13v3.5859L3.2929,19.293A1,1,0,0,0,3,20v3a1,1,0,0,0,1,1h7v.7768a5.152,5.152,0,0,0,4.5,5.1987A5.0057,5.0057,0,0,0,21,25V24h7a1,1,0,0,0,1-1V20A1,1,0,0,0,28.7071,19.293ZM19,25a3,3,0,0,1-6,0V24h6Zm8-3H5V20.4141L7.707,17.707A1,1,0,0,0,8,17V13a8,8,0,0,1,16,0v4a1,1,0,0,0,.293.707L27,20.4141Z" />
    </svg>
  );
}

/* =========================================================================
 * 3. SwitcherIcon — viewBox 0 0 32 32 (3x3 rutenett)
 * ========================================================================= */
export function SwitcherIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M14 4H18V8H14z" />
      <path d="M4 4H8V8H4z" />
      <path d="M24 4H28V8H24z" />
      <path d="M14 14H18V18H14z" />
      <path d="M4 14H8V18H4z" />
      <path d="M24 14H28V18H24z" />
      <path d="M14 24H18V28H14z" />
      <path d="M4 24H8V28H4z" />
      <path d="M24 24H28V28H24z" />
    </svg>
  );
}

/* =========================================================================
 * 4. MenuIcon — viewBox 0 0 20 20 (hamburgerlinjer)
 * ========================================================================= */
export function MenuIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M2 14.8H18V16H2z" />
      <path d="M2 11.2H18V12.399999999999999H2z" />
      <path d="M2 7.6H18V8.799999999999999H2z" />
      <path d="M2 4H18V5.2H2z" />
    </svg>
  );
}

/* =========================================================================
 * 5. LoginIcon — viewBox 0 0 32 32 (dør med pil inn)
 * ========================================================================= */
export function LoginIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M26,30H14a2,2,0,0,1-2-2V25h2v3H26V4H14V7H12V4a2,2,0,0,1,2-2H26a2,2,0,0,1,2,2V28A2,2,0,0,1,26,30Z" />
      <path d="M14.59 20.59 18.17 17 4 17 4 15 18.17 15 14.59 11.41 16 10 22 16 16 22 14.59 20.59z" />
    </svg>
  );
}

/* =========================================================================
 * 6. InfoIcon — viewBox 0 0 32 32 (sirkel med "i")
 * Placeholder-ikon for ny header-action (dummy-funksjonalitet).
 * ========================================================================= */
export function InfoIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M16,2A14,14,0,1,0,30,16,14.0158,14.0158,0,0,0,16,2Zm0,26A12,12,0,1,1,28,16,12.0137,12.0137,0,0,1,16,28Z" />
      <path d="M14 14H16V22H14z" />
      <path d="M15 8a2 2 0 1 0 2 2 2 2 0 0 0-2-2z" />
    </svg>
  );
}

/* =========================================================================
 * 6. UserIcon — viewBox 0 0 32 32 (bruker/silhuett)
 * ========================================================================= */
export function UserIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M16 30H4a2 2 0 0 1-2-2v-3a4.985 4.985 0 0 1 3.836-4.858 11.985 11.985 0 0 1 9.164 0A4.985 4.985 0 0 1 18 25v3a2 2 0 0 1-2 2Zm-9-5v1h9v-1a2.982 2.982 0 0 0-2.235-2.884 9.985 9.985 0 0 0-4.53 0A2.982 2.982 0 0 0 7 25Z" />
      <path d="M16 7a5 5 0 1 1-5 5 5 5 0 0 1 5-5m0-2a7 7 0 1 0 7 7 7 7 0 0 0-7-7Z" />
      <path d="M26 4h-6V2h6v8h2V6a2 2 0 0 0-2-2Z" />
      <path d="M28 12H22v2h6v8h2V14a2 2 0 0 0-2-2Z" />
    </svg>
  );
}

/* =========================================================================
 * 7. RoleIcon — viewBox 0 0 32 32 (nøkkel — representerer rolle/tilgang)
 * ========================================================================= */
export function RoleIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M24 14a8 8 0 1 0-8 8 8.009 8.009 0 0 0 8-8Zm-8 6a6 6 0 1 1 6-6 6.007 6.007 0 0 1-6 6Z" />
      <path d="M12.023 21.43 4.573 14.38a2 2 0 0 1 0-2.83l8.4-8.4A2 2 0 0 1 15.4 3.15l8.4 8.4a2 2 0 0 1 0 2.83l-8.4 8.4a2 2 0 0 1-2.83 0Z" />
      <path d="M10.6 12.6 8.4 10.4 16 2.8l2.2 2.2-7.6 7.6Z" />
    </svg>
  );
}

/* =========================================================================
 * 8. PermissionIcon — viewBox 0 0 32 32 (lås — representerer tillatelse)
 * ========================================================================= */
export function PermissionIcon({ size = 20, ...props }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M24 14h-1V9a7 7 0 0 0-14 0v5h-1a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V16a2 2 0 0 0-2-2Zm-13-5a5 5 0 0 1 10 0v5h-10Zm13 18H9V16h15Z" />
      <path d="M16 19.5a2.5 2.5 0 0 1 1 4.77V27a1 1 0 0 1-2 0v-2.73A2.5 2.5 0 0 1 16 19.5Z" />
    </svg>
  );
}
